import React, { useState } from 'react';
import { authenticatedFetch } from '../../../utils/auth';
import AutoSellOptionsModal from '../modals/AutoSellOptionsModal';

const PortfolioTab = ({
  aiTradeStatus,
  setShowScheduledOrderModal,
  setSelectedScheduledOrder,
  // 주문 탭으로 이동 및 종목 선택 관련
  setSelectedStock,
  fetchStockData,
  setActiveTab,
  fetchPortfolio,
  portfolio,
  portfolioLoading,
  scheduledOrders,
  fetchScheduledOrders,
  cancelScheduledOrder,
  authError,
  autoTrades,
  setSelectedAutoTrade,
  setShowAutoTradeModal,
  user,
  fetchAiTradeStatus,
  fetchAutoTrades,
  // 주문 실행 함수
  executeOrder,
  setOrderType,
  setQuantity,
  setPrice,
  setOrderMethod
}) => {
  const [resetting, setResetting] = useState(false);
  const [toggling, setToggling] = useState(false);
  // 예약 주문 섹션 표시/숨김 상태 (기본값: false = 숨김)
  const [showScheduledOrders, setShowScheduledOrders] = useState(false);
  const [lossOver10Percent, setLossOver10Percent] = useState([]);
  const [loadingLossData, setLoadingLossData] = useState(false);
  const [showLossData, setShowLossData] = useState(false);
  const [showAutoSellModal, setShowAutoSellModal] = useState(false);
  const [selectedSymbolForAutoSell, setSelectedSymbolForAutoSell] = useState(null);
  const [selectedStockNameForAutoSell, setSelectedStockNameForAutoSell] = useState(null);
  const [aiTradeConfig, setAiTradeConfig] = useState(null);
  const [savingProfitTargetMode, setSavingProfitTargetMode] = useState(null);
  
  const isPaperTrading = user?.trading?.is_paper_trading !== false;

  const handleToggleAiExclude = async (symbol, excluded) => {
    try {
      const response = await authenticatedFetch('/api/ai-trade/exclude-symbol', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          symbol,
          excluded
        })
      });
      if (response.ok) {
        await fetchPortfolio();
        if (fetchAiTradeStatus) {
          await fetchAiTradeStatus();
        }
      } else {
        const error = await response.json();
        alert(error.error || '설정 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('AI 거래 제외 설정 실패:', error);
      alert('설정 저장 중 오류가 발생했습니다.');
    }
  };
  
  // AI 거래 설정 조회 (종목별 수익목표 매도 방식 표시/저장용)
  React.useEffect(() => {
    if (!portfolio || portfolio.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await authenticatedFetch('/api/ai-trade/config');
        if (!response.ok || cancelled) return;
        const data = await response.json();
        if (!cancelled && data.config) setAiTradeConfig(data.config);
      } catch (err) {
        if (!cancelled) console.error('AI config fetch:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [portfolio]);

  const handleSellOnDownturnChange = async (symbol, checked) => {
    const key = (symbol || '').toUpperCase();
    setSavingProfitTargetMode(key);
    try {
      const list = Array.isArray(aiTradeConfig?.symbol_configs) ? [...aiTradeConfig.symbol_configs] : [];
      const idx = list.findIndex(sc => (sc.symbol || '').toUpperCase() === key);
      if (idx >= 0) {
        list[idx] = { ...list[idx], sell_on_downturn: !!checked };
      } else {
        list.push({ symbol: symbol?.toUpperCase() || symbol, sell_on_downturn: !!checked });
      }
      const response = await authenticatedFetch('/api/ai-trade/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol_configs: list })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.config) setAiTradeConfig(data.config);
        if (fetchAiTradeStatus) fetchAiTradeStatus();
      } else {
        const err = await response.json();
        alert(err.error || '설정 저장에 실패했습니다.');
      }
    } catch (e) {
      console.error('sell_on_downturn save:', e);
      alert('설정 저장 중 오류가 발생했습니다.');
    } finally {
      setSavingProfitTargetMode(null);
    }
  };

  // 손실 10% 초과 종목 조회
  const fetchLossOver10Percent = async () => {
    setLoadingLossData(true);
    try {
      const response = await authenticatedFetch('/api/portfolio/loss-over-10-percent');
      if (response.ok) {
        const data = await response.json();
        setLossOver10Percent(data.items || []);
        setShowLossData(true);
      } else {
        console.error('손실 10% 초과 종목 조회 실패');
      }
    } catch (error) {
      console.error('손실 10% 초과 종목 조회 오류:', error);
    } finally {
      setLoadingLossData(false);
    }
  };
  
  // scheduledOrders가 배열인지 확인하고 기본값 설정
  const safeScheduledOrders = Array.isArray(scheduledOrders) ? scheduledOrders : [];
  
  // 디버깅: 예약 주문 섹션 렌더링 확인 (개발 모드에서만)
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('📅 [예약 주문 섹션] 렌더링 상태:', {
        showScheduledOrders,
        scheduledOrdersCount: safeScheduledOrders.length,
        hasScheduledOrders: Array.isArray(scheduledOrders),
        scheduledOrdersType: typeof scheduledOrders
      });
    }
  }, [showScheduledOrders, scheduledOrders]);
  
  // 디버깅: props 확인 (개발 모드에서만)
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('📋 PortfolioTab 렌더링:', {
        hasUser: !!user,
        isPaperTrading,
        hasAutoTrades: Array.isArray(autoTrades),
        autoTradesLength: Array.isArray(autoTrades) ? autoTrades.length : 0,
        hasAiTradeStatus: !!aiTradeStatus,
        aiTradeStatusIsActive: aiTradeStatus?.is_active,
        hasFetchFunctions: {
          fetchAiTradeStatus: !!fetchAiTradeStatus,
          fetchAutoTrades: !!fetchAutoTrades
        }
      });
    }
  }, [user, isPaperTrading, autoTrades, aiTradeStatus, fetchAiTradeStatus, fetchAutoTrades]);
  
  // 모의투자 초기화
  const handleResetPaperTrading = async () => {
    if (!window.confirm('모의투자 데이터를 초기화하시겠습니까?\n\n초기화 항목:\n- 포트폴리오\n- 주문 내역\n- 자동거래 설정 (비활성화)\n- AI 거래 설정 (비활성화)\n- 예약 주문\n\n이 작업은 되돌릴 수 없습니다.')) {
      return;
    }
    
    // 토큰 확인
    const { getToken } = await import('../../../utils/auth');
    const token = getToken();
    if (!token) {
      alert('인증 토큰이 없습니다. 다시 로그인해주세요.');
      return;
    }
    
    setResetting(true);
    try {
      const response = await authenticatedFetch('/api/portfolio/reset-paper-trading', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(data.message || '모의투자 데이터가 초기화되었습니다.');
        // 데이터 새로고침
        fetchPortfolio();
        fetchScheduledOrders();
        if (fetchAiTradeStatus) fetchAiTradeStatus();
        if (fetchAutoTrades) fetchAutoTrades();
      } else {
        const error = await response.json();
        alert(error.error || '초기화에 실패했습니다.');
      }
    } catch (error) {
      console.error('초기화 실패:', error);
      alert('초기화 중 오류가 발생했습니다.');
    } finally {
      setResetting(false);
    }
  };
  
  // 자동거래/AI거래 일시정지/재개
  const handleToggleAutoTrade = async (type, action) => {
    const actionText = action === 'pause' ? '일시정지' : '재개';
    const typeText = type === 'auto' ? '자동거래' : 'AI거래';
    
    if (!window.confirm(`${typeText}를 ${actionText}하시겠습니까?`)) {
      return;
    }
    
    setToggling(true);
    try {
      const response = await authenticatedFetch('/api/portfolio/toggle-auto-trade', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, action })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(data.message || `${typeText}가 ${actionText}되었습니다.`);
        // 데이터 새로고침
        fetchPortfolio();
        if (fetchAiTradeStatus) fetchAiTradeStatus();
        if (fetchAutoTrades) fetchAutoTrades();
      } else {
        const error = await response.json();
        alert(error.error || `${actionText}에 실패했습니다.`);
      }
    } catch (error) {
      console.error('일시정지/재개 실패:', error);
      alert(`${actionText} 중 오류가 발생했습니다.`);
    } finally {
      setToggling(false);
    }
  };
  
  // 자동거래 활성화 여부 확인 (안전하게 처리)
  const hasActiveAutoTrades = Array.isArray(autoTrades) && autoTrades.length > 0 && autoTrades.some(trade => trade.is_active);
  
  return (
    <div className="portfolio-tab">
      {/* AI 자동거래 활성화 안내 메시지 */}
      {aiTradeStatus && aiTradeStatus.is_active && (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#fff3cd', 
          borderRadius: '8px', 
          border: '1px solid #ffc107', 
          marginBottom: '20px' 
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '10px', color: '#856404' }}>
            ⚠️ AI 자동거래 활성화 중
          </div>
          <div style={{ fontSize: '14px', color: '#856404', marginBottom: '10px' }}>
            AI 자동거래가 활성화되어 있어 일반 자동거래 설정이 비활성화됩니다.
            AI가 주단위로 분석하여 최적의 거래 시점과 수량을 결정합니다.
          </div>
          {aiTradeStatus.auto_buy && aiTradeStatus.auto_buy.enabled && (
            <div style={{ 
              marginTop: '10px', 
              padding: '10px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '5px',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '5px', color: '#495057' }}>
                💰 잔고 제한 설정
              </div>
              <div style={{ fontSize: '13px', color: '#6c757d', lineHeight: '1.6' }}>
                • 최대 사용 비율: {aiTradeStatus.auto_buy.balance_limits?.max_balance_usage_percent || 80}%
                {aiTradeStatus.auto_buy.balance_limits?.reserved_balance > 0 && (
                  <>
                    <br />
                    • 예약 잔고: ₩{aiTradeStatus.auto_buy.balance_limits.reserved_balance.toLocaleString()}
                  </>
                )}
                <br />
                <small style={{ color: '#868e96' }}>
                  AI 거래는 설정된 잔고 제한 내에서만 실행됩니다.
                </small>
              </div>
            </div>
          )}
        </div>
      )}
      
      
      {/* 포트폴리오 현재 상태 요약 (탭 메뉴 하단 독립 표시) */}
      {(() => {
        const validPortfolio = Array.isArray(portfolio) ? portfolio : [];
        const holdingsCount = validPortfolio.length;
        const purchaseTotal = validPortfolio.reduce((sum, item) => {
          const quantity = Number(item.quantity || item.qty || 0);
          // 평균가 필드명 통일: averagePrice, avgPrice, avg_price, average_price
          const averagePrice = Number(item.averagePrice || item.avgPrice || item.avg_price || item.average_price || 0);
          const purchaseAmount = quantity * averagePrice;
          
          // 디버깅: 구매총액 계산 확인
          if (process.env.NODE_ENV === 'development' && quantity > 0 && averagePrice > 0) {
            console.log(`[구매총액 계산] ${item.symbol}: 수량=${quantity}, 평균가=${averagePrice}, 구매금액=${purchaseAmount}`);
          }
          
          return sum + purchaseAmount;
        }, 0);
        
        const evaluationTotal = validPortfolio.reduce((sum, item) => {
          const quantity = Number(item.quantity || item.qty || 0);
          // 현재가 필드명 통일: currentPrice, current_price, price
          const currentPrice = Number(item.currentPrice || item.current_price || item.price || 0);
          const evaluationAmount = quantity * currentPrice;
          
          // 디버깅: 평가총액 계산 확인
          if (process.env.NODE_ENV === 'development' && quantity > 0 && currentPrice > 0) {
            console.log(`[평가총액 계산] ${item.symbol}: 수량=${quantity}, 현재가=${currentPrice}, 평가금액=${evaluationAmount}`);
          }
          
          return sum + evaluationAmount;
        }, 0);
        
        const profitLossTotal = evaluationTotal - purchaseTotal; // 손익총액
        
        // 디버깅: 총액 계산 확인
        if (process.env.NODE_ENV === 'development') {
          console.log('📊 포트폴리오 총액 계산:', {
            보유종목수: validPortfolio.length,
            구매총액: purchaseTotal,
            평가총액: evaluationTotal,
            손익총액: profitLossTotal,
            계산식: `${evaluationTotal} - ${purchaseTotal} = ${profitLossTotal}`
          });
        }
        
        // 항상 표시 (포트폴리오가 비어있어도 0으로 표시)
        return (
          <div style={{
            marginBottom: '20px',
            padding: '15px 20px',
            backgroundColor: '#f0f8ff',
            borderRadius: '8px',
            border: '2px solid #007bff',
            display: 'flex',
            gap: '40px',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
              <div style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>보유종목수</div>
              <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#007bff' }}>
                {holdingsCount}개
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
              <div style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>구매총액</div>
              <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#333' }}>
                ₩{Math.round(purchaseTotal).toLocaleString()}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
              <div style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>평가총액</div>
              <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#28a745' }}>
                ₩{Math.round(evaluationTotal).toLocaleString()}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
              <div style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>손익총액</div>
              <div style={{ 
                fontSize: '22px', 
                fontWeight: 'bold', 
                color: profitLossTotal >= 0 ? '#28a745' : '#dc3545'
              }}>
                {profitLossTotal >= 0 ? '+' : ''}₩{Math.round(profitLossTotal).toLocaleString()}
              </div>
            </div>
          </div>
        );
      })()}
      
      <div className="portfolio-header">
        <h2>보유 종목</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* 자동거래/AI거래 일시정지/재개 버튼 */}
          <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
            {/* 자동거래 버튼 - 항상 표시 */}
            {hasActiveAutoTrades ? (
              <button
                onClick={() => handleToggleAutoTrade('auto', 'pause')}
                disabled={toggling}
                style={{
                  padding: '8px 16px',
                  backgroundColor: toggling ? '#ccc' : '#ffc107',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: toggling ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                title="자동거래 일시정지"
              >
                ⏸️ 자동거래 일시정지
              </button>
            ) : (
              <button
                onClick={() => handleToggleAutoTrade('auto', 'resume')}
                disabled={toggling}
                style={{
                  padding: '8px 16px',
                  backgroundColor: toggling ? '#ccc' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: toggling ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                title="자동거래 재개"
              >
                ▶️ 자동거래 재개
              </button>
            )}
            
            {/* AI거래 버튼 - 항상 표시 */}
            {aiTradeStatus && aiTradeStatus.is_active ? (
              <button
                onClick={() => handleToggleAutoTrade('ai', 'pause')}
                disabled={toggling}
                style={{
                  padding: '8px 16px',
                  backgroundColor: toggling ? '#ccc' : '#ffc107',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: toggling ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                title="AI거래 일시정지"
              >
                ⏸️ AI거래 일시정지
              </button>
            ) : (
              <button
                onClick={() => handleToggleAutoTrade('ai', 'resume')}
                disabled={toggling || !(aiTradeStatus && aiTradeStatus.has_api_key)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: toggling || !(aiTradeStatus && aiTradeStatus.has_api_key) ? '#ccc' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: toggling || !(aiTradeStatus && aiTradeStatus.has_api_key) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                title={!(aiTradeStatus && aiTradeStatus.has_api_key) ? 'AI 거래를 재개하려면 Google API 키가 필요합니다.' : 'AI거래 재개'}
              >
                ▶️ AI거래 재개
              </button>
            )}
          </div>
          
          <button 
            onClick={() => {
              setShowScheduledOrderModal(true);
              setSelectedScheduledOrder(null);
            }}
            style={{ 
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#218838'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#28a745'}
          >
            📅 예약 주문 추가
          </button>
          
          {/* 모의투자 초기화 버튼 (모의투자일 때만 표시) */}
          {isPaperTrading && (
            <button
              onClick={handleResetPaperTrading}
              disabled={resetting}
              style={{
                padding: '8px 16px',
                backgroundColor: resetting ? '#ccc' : '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: resetting ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
              title="모의투자 데이터 초기화"
            >
              {resetting ? '초기화 중...' : '🔄 모의투자 초기화'}
            </button>
          )}
          
          <button 
            onClick={async () => {
              console.log('🔄 포트폴리오 새로고침 버튼 클릭');
              await fetchPortfolio(true); // 강제 새로고침
            }} 
            disabled={portfolioLoading}
            className="btn-refresh"
            style={{
              padding: '8px 16px',
              backgroundColor: portfolioLoading ? '#6c757d' : '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: portfolioLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              opacity: portfolioLoading ? 0.6 : 1
            }}
            onMouseOver={(e) => {
              if (!portfolioLoading) {
                e.target.style.backgroundColor = '#138496';
              }
            }}
            onMouseOut={(e) => {
              if (!portfolioLoading) {
                e.target.style.backgroundColor = '#17a2b8';
              }
            }}
            title={portfolioLoading ? '갱신 중...' : '포트폴리오 새로고침'}
          >
            {portfolioLoading ? '⏳ 갱신 중...' : '🔄 새로고침'}
          </button>
        </div>
      </div>
      
      {/* 보유 종목 섹션 */}
      {authError ? (
        <div className="portfolio-empty">
          <p>로그인이 필요합니다.</p>
        </div>
      ) : (
        <>
          {/* 손실 10% 초과 종목 섹션 */}
          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 style={{ margin: 0, color: '#856404' }}>⚠️ 손실 10% 초과 종목</h3>
              <div>
                <button
                  onClick={fetchLossOver10Percent}
                  disabled={loadingLossData}
                  style={{
                    padding: '6px 12px',
                    fontSize: '14px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loadingLossData ? 'not-allowed' : 'pointer',
                    marginRight: '8px'
                  }}
                >
                  {loadingLossData ? '조회 중...' : '조회'}
                </button>
                {showLossData && (
                  <button
                    onClick={() => setShowLossData(false)}
                    style={{
                      padding: '6px 12px',
                      fontSize: '14px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    숨기기
                  </button>
                )}
              </div>
            </div>
            {showLossData && lossOver10Percent.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                {lossOver10Percent.map((item, index) => (
                  <div key={index} style={{ marginBottom: '15px', padding: '12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #ffc107' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                      {item.name || item.stock_name || item.stockName || '종목명 없음'} ({item.symbol}) - {item.profitLossRate.toFixed(2)}% 손실
                    </div>
                    <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                      <div>보유수량: {item.quantity}주</div>
                      <div>평균단가: ₩{item.averagePrice.toLocaleString()}</div>
                      <div>현재가: ₩{item.currentPrice.toLocaleString()}</div>
                      <div>손실금액: ₩{Math.abs(item.profitLoss).toLocaleString()}</div>
                      {item.stopLossPrice > 0 && (
                        <div>손절가: ₩{item.stopLossPrice.toLocaleString()}</div>
                      )}
                    </div>
                    {item.scheduledOrders && item.scheduledOrders.length > 0 && (
                      <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#e7f3ff', borderRadius: '4px' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>예약 주문:</div>
                        {item.scheduledOrders.map((order, idx) => (
                          <div key={idx} style={{ fontSize: '12px' }}>
                            {order.quantity}주 @ ₩{order.price.toLocaleString()} - {new Date(order.scheduled_time).toLocaleString('ko-KR')} ({order.status})
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f8d7da', borderRadius: '4px' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>미매도 이유:</div>
                      <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px' }}>
                        {item.reasons.map((reason, idx) => (
                          <li key={idx}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {showLossData && lossOver10Percent.length === 0 && (
              <div style={{ marginTop: '10px', color: '#856404' }}>
                손실 10%를 넘는 종목이 없습니다.
              </div>
            )}
          </div>
          
          {!Array.isArray(portfolio) || portfolio.length === 0 ? (
            <div className="portfolio-empty">
              <p>보유 주식이 없습니다.</p>
            </div>
          ) : (
            <div className="portfolio-list">
          {portfolio.map((item, index) => {
            const autoTrade = autoTrades.find(at => at.symbol === item.symbol);
            return (
              <div 
                key={index} 
                className="portfolio-item" 
                style={{ position: 'relative', cursor: 'pointer' }}
                onClick={async () => {
                  if (item.symbol && setSelectedStock && fetchStockData && setActiveTab) {
                    // 주문 탭으로 이동
                    setActiveTab('trade');
                    // 종목 선택 및 데이터 조회
                    setSelectedStock(item.symbol);
                    await fetchStockData(item.symbol);
                  }
                }}
                title="클릭하여 주문 탭에서 이 종목 확인"
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                    <div className="portfolio-name" style={{
                      flex: 1,
                      fontWeight: '500',
                      color: item.name || item.stock_name || item.stockName ? '#333' : '#999',
                      fontStyle: item.name || item.stock_name || item.stockName ? 'normal' : 'italic'
                    }}>
                      {item.name || item.stock_name || item.stockName || (
                        <span style={{ fontSize: '12px', color: '#999' }}>
                          종목명 조회 중...
                        </span>
                      )}
                    </div>
                    <div className="portfolio-symbol" style={{ 
                      fontWeight: 'normal', 
                      color: '#666',
                      fontSize: '13px',
                      minWidth: '60px'
                    }}>
                      ({item.symbol || item.code || 'N/A'})
                    </div>
                  </div>
                </div>
                <div className="portfolio-details">
                  <div>보유수량: {item.quantity || 0}주</div>
                  <div>평균단가: ₩{(() => {
                    // 평균가 필드명 통일
                    const avgPrice = item.averagePrice || item.avgPrice || item.avg_price || item.average_price || 0;
                    if (avgPrice && avgPrice > 0) {
                      return parseInt(avgPrice).toLocaleString();
                    }
                    return '계산 중...';
                  })()}</div>
                  <div>현재가: ₩{(() => {
                    // 현재가 필드명 통일
                    const currentPrice = item.currentPrice || item.current_price || item.price || 0;
                    if (currentPrice && currentPrice > 0) {
                      return parseInt(currentPrice).toLocaleString();
                    }
                    return 'N/A';
                  })()}</div>
                  <div>평가금액: ₩{(() => {
                    // 평가금액 계산 (수량 * 현재가)
                    const quantity = item.quantity || item.qty || 0;
                    const currentPrice = item.currentPrice || item.current_price || item.price || 0;
                    const totalValue = item.totalValue || item.total_value || (quantity * currentPrice);
                    if (totalValue && totalValue > 0) {
                      return parseInt(totalValue).toLocaleString();
                    }
                    return 'N/A';
                  })()}</div>
                  <div className={`profit ${(item.profitLoss || item.profit || 0) >= 0 ? 'positive' : 'negative'}`}>
                    손익: {(() => {
                      // 평균단가 확인 (필드명 통일)
                      const avgPrice = item.averagePrice || item.avgPrice || item.avg_price || item.average_price || 0;
                      const currentPrice = item.currentPrice || item.current_price || item.price || 0;
                      const quantity = item.quantity || item.qty || 0;
                      
                      // 디버깅: 포트폴리오 항목 데이터 확인
                      if (process.env.NODE_ENV === 'development') {
                        console.log(`🔍 [${item.symbol}] 포트폴리오 항목 데이터:`, {
                          symbol: item.symbol,
                          name: item.name,
                          averagePrice: item.averagePrice,
                          avgPrice: item.avgPrice,
                          currentPrice: item.currentPrice,
                          quantity: item.quantity,
                          profitLoss: item.profitLoss,
                          profit: item.profit,
                          profitLossRate: item.profitLossRate,
                          profitRate: item.profitRate
                        });
                      }
                      
                      // 평균단가와 현재가가 모두 있고 보유수량이 있으면 손익 계산 가능
                      if (avgPrice && avgPrice > 0 && currentPrice && currentPrice > 0 && quantity && quantity > 0) {
                        // 백엔드에서 계산된 손익 값 확인
                        const profitLoss = (item.profitLoss !== undefined && item.profitLoss !== null && !isNaN(item.profitLoss)) 
                          ? item.profitLoss 
                          : (item.profit !== undefined && item.profit !== null && !isNaN(item.profit) ? item.profit : null);
                          
                        const profitLossRate = (item.profitLossRate !== undefined && !isNaN(item.profitLossRate))
                          ? item.profitLossRate
                          : (item.profitRate !== undefined && !isNaN(item.profitRate) ? item.profitRate : null);
                          
                        // 백엔드에서 계산된 값이 있으면 사용, 없으면 프론트엔드에서 계산
                        let finalProfitLoss = profitLoss;
                        let finalProfitLossRate = profitLossRate;
                        
                        if (finalProfitLoss === null || finalProfitLossRate === null || isNaN(finalProfitLoss) || isNaN(finalProfitLossRate)) {
                          // 프론트엔드에서 직접 계산
                          finalProfitLoss = (currentPrice - avgPrice) * quantity;
                          finalProfitLossRate = ((currentPrice - avgPrice) / avgPrice) * 100;
                        }
                        
                        return (
                          <>
                            {finalProfitLoss >= 0 ? '+' : ''}₩{Math.abs(Math.round(finalProfitLoss)).toLocaleString()}
                            {' '}
                            ({finalProfitLossRate >= 0 ? '+' : ''}{finalProfitLossRate.toFixed(2)}%)
                          </>
                        );
                      } else {
                        // 손익 계산 불가능한 경우
                        return (
                          <span style={{ color: '#999', fontStyle: 'italic' }}>
                            계산 불가 {
                              !(avgPrice && avgPrice > 0) ? '(평균단가 없음)' :
                              !(currentPrice && currentPrice > 0) ? '(현재가 없음)' :
                              !(quantity && quantity > 0) ? '(보유수량 없음)' : ''
                            }
                          </span>
                        );
                      }
                    })()}
                  </div>
                  {/* 매도/매수 버튼 및 AI 거래 설정 배지 */}
                  <div style={{ 
                    marginTop: '10px', 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}>
                    {/* 매도/매수 버튼 */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {item.quantity > 0 && (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (setSelectedStock && setOrderType && setQuantity && setPrice && setOrderMethod && setActiveTab) {
                              setSelectedStock(item.symbol);
                              setOrderType('sell');
                              setQuantity(item.quantity.toString());
                              setPrice(item.currentPrice ? item.currentPrice.toString() : '');
                              setOrderMethod('limit');
                              setActiveTab('trade');
                              if (fetchStockData) {
                                await fetchStockData(item.symbol);
                              }
                            }
                          }}
                          style={{
                            padding: '4px 12px',
                            fontSize: '12px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: '500'
                          }}
                          title={`${item.name || item.symbol} ${item.quantity}주 매도`}
                        >
                          매도
                        </button>
                      )}
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (setSelectedStock && setOrderType && setQuantity && setPrice && setOrderMethod && setActiveTab) {
                            setSelectedStock(item.symbol);
                            setOrderType('buy');
                            setQuantity('');
                            setPrice(item.currentPrice ? item.currentPrice.toString() : '');
                            setOrderMethod('limit');
                            setActiveTab('trade');
                            if (fetchStockData) {
                              await fetchStockData(item.symbol);
                            }
                          }
                        }}
                        style={{
                          padding: '4px 12px',
                          fontSize: '12px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                        title={`${item.name || item.symbol} 매수`}
                      >
                        매수
                      </button>
                    </div>
                    
                    {/* AI 거래 설정 배지 및 자동매도 옵션 버튼 */}
                    {item.has_ai_trade_config && (
                      <>
                        <div style={{ 
                          padding: '6px 10px', 
                          backgroundColor: '#667eea', 
                          borderRadius: '4px', 
                          fontSize: '12px',
                          color: 'white',
                          fontWeight: '600',
                          display: 'inline-block'
                        }}>
                          🤖 AI거래설정(됨)
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSymbolForAutoSell(item.symbol);
                            setSelectedStockNameForAutoSell(item.name || item.stock_name || item.symbol);
                            setShowAutoSellModal(true);
                          }}
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title={`${item.name || item.symbol} AI 자동매도 옵션 확인`}
                        >
                          📊 자동매도 옵션
                        </button>
                      </>
                    )}
                  </div>
                  
                  {/* AI 거래 예외 설정 - 항상 표시 */}
                  <div style={{ 
                    marginTop: '10px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px' 
                  }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      fontSize: '13px',
                      color: '#666'
                    }}>
                      <input
                        type="checkbox"
                        checked={item.ai_trade_excluded || false}
                          onChange={(e) => handleToggleAiExclude(item.symbol, e.target.checked)}
                        style={{
                          marginRight: '6px',
                          cursor: 'pointer',
                          width: '16px',
                          height: '16px'
                        }}
                      />
                      <span style={{ 
                        color: item.ai_trade_excluded ? '#dc3545' : '#666',
                        fontWeight: item.ai_trade_excluded ? '600' : 'normal'
                      }}>
                        {item.ai_trade_excluded ? '🚫 AI거래 예외' : 'AI거래 예외'}
                      </span>
                    </label>
                  </div>

                  {/* 하향 전환 시 매도 (수익목표 무관) */}
                  <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '13px', color: '#666' }}>
                      <input
                        type="checkbox"
                        checked={!!(aiTradeConfig?.symbol_configs || []).find(sc => (sc.symbol || '').toUpperCase() === (item.symbol || '').toUpperCase())?.sell_on_downturn}
                        onChange={(e) => handleSellOnDownturnChange(item.symbol, e.target.checked)}
                        disabled={savingProfitTargetMode === (item.symbol || '').toUpperCase()}
                        style={{ marginRight: '6px', cursor: 'pointer', width: '16px', height: '16px' }}
                      />
                      <span>하향 전환 시 매도 (수익목표 무관)</span>
                    </label>
                    {savingProfitTargetMode === (item.symbol || '').toUpperCase() && (
                      <span style={{ fontSize: '12px', color: '#666' }}>저장 중...</span>
                    )}
                  </div>
                  
                  {/* 자동거래 설정 표시 */}
                  {item.has_auto_trade && (
                    <div style={{ 
                      marginTop: '10px', 
                      padding: '8px', 
                      backgroundColor: item.auto_trade_status === '중단(대기)' ? '#fff3cd' : '#e7f3ff', 
                      borderRadius: '4px', 
                      fontSize: '12px',
                      border: item.auto_trade_status === '중단(대기)' ? '1px solid #ffc107' : 'none'
                    }}>
                      <div style={{ 
                        fontWeight: '600',
                        color: item.auto_trade_status === '중단(대기)' ? '#856404' : '#333',
                        marginBottom: '5px'
                      }}>
                        ⚙️ 자동거래: {item.auto_trade_status}
                        {item.auto_trade_status === '중단(대기)' && (
                          <span style={{ fontSize: '11px', marginLeft: '5px' }}>
                            (AI거래설정 활성화로 인해 대기 중)
                          </span>
                        )}
                      </div>
                      {autoTrade && autoTrade.is_active && item.auto_trade_status !== '중단(대기)' && (
                        <>
                          {autoTrade.buy_enabled && (
                            <div>매수: 기준가 대비 {autoTrade.buy_trigger_percent}% 하락 시 주문가능현금의 {autoTrade.buy_quantity_percent}% 매수</div>
                          )}
                          {autoTrade.sell_enabled && (
                            <div>매도: 기준가 대비 {autoTrade.sell_trigger_percent}% 상승 시 보유수량의 {autoTrade.sell_quantity_percent}% 매도</div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSelectedAutoTrade(autoTrade || { symbol: item.symbol, stock_name: item.name });
                    setShowAutoTradeModal(true);
                  }}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    backgroundColor: autoTrade && autoTrade.is_active ? '#28a745' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {autoTrade && autoTrade.is_active ? '⚙️ 자동거래 설정' : '➕ 자동거래 설정'}
                </button>
              </div>
            );
          })}
            </div>
          )}
        </>
      )}
      
      {/* 예약 주문 목록 섹션 - 항상 표시 */}
      <div style={{ 
        marginTop: '20px', 
        marginBottom: '20px', 
        padding: '15px', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px', 
        border: '1px solid #dee2e6',
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '10px', 
          flexWrap: 'wrap', 
          gap: '10px',
          position: 'relative',
          zIndex: 2
        }}>
          <h3 style={{ margin: 0, flex: '1 1 auto' }}>📅 예약 주문</h3>
          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            alignItems: 'center', 
            flexShrink: 0,
            position: 'relative',
            zIndex: 3
          }}>
            <button 
              onClick={fetchScheduledOrders} 
              style={{ 
                padding: '8px 16px', 
                fontSize: '13px',
                backgroundColor: '#17a2b8', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: 'pointer',
                fontWeight: '500',
                whiteSpace: 'nowrap',
                minWidth: '60px',
                display: 'inline-block',
                visibility: 'visible',
                opacity: 1
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#138496'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#17a2b8'}
            >
              🔄 조회
            </button>
            <button 
              id="scheduled-orders-toggle-button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('👁️ [예약 주문] 감추기/보이기 버튼 클릭:', { 
                  현재상태: showScheduledOrders,
                  버튼ID: 'scheduled-orders-toggle-button',
                  timestamp: new Date().toISOString()
                });
                setShowScheduledOrders(prev => {
                  const newState = !prev;
                  console.log('👁️ [예약 주문] 상태 변경:', { 이전: prev, 이후: newState });
                  return newState;
                });
              }} 
              style={{ 
                padding: '8px 16px', 
                fontSize: '13px',
                backgroundColor: showScheduledOrders ? '#6c757d' : '#28a745', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: 'pointer',
                fontWeight: '500',
                whiteSpace: 'nowrap',
                minWidth: '80px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                display: 'inline-block',
                visibility: 'visible',
                opacity: 1,
                position: 'relative',
                zIndex: 10
              }}
              title={showScheduledOrders ? '예약 주문 섹션 숨기기' : '예약 주문 섹션 보이기'}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = showScheduledOrders ? '#5a6268' : '#218838';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = showScheduledOrders ? '#6c757d' : '#28a745';
                e.target.style.transform = 'scale(1)';
              }}
            >
              {showScheduledOrders ? '👁️ 감추기' : '👁️ 보이기'}
            </button>
          </div>
        </div>
        {showScheduledOrders && (
          <>
            {safeScheduledOrders.length > 0 ? (
              <div style={{ display: 'grid', gap: '10px' }}>
                {safeScheduledOrders.map((order) => (
                  <div 
                    key={order._id} 
                    style={{ 
                      padding: '12px', 
                      backgroundColor: 'white', 
                      borderRadius: '6px', 
                      border: '1px solid #dee2e6',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                        {order.stock_name || order.symbol} - {order.order_type === 'buy' ? '매수' : '매도'}
                      </div>
                      <div style={{ fontSize: '13px', color: '#666' }}>
                        수량: {order.quantity}주, 가격: ₩{order.price?.toLocaleString() || 'N/A'}
                      </div>
                      <div style={{ fontSize: '13px', color: '#666' }}>
                        예약시간: {new Date(order.scheduled_time).toLocaleString('ko-KR')}
                      </div>
                      {order.repeat_type !== 'none' && (
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          반복: {order.repeat_type === 'daily' ? '매일' : order.repeat_type === 'weekly' ? '매주' : '매월'} 
                          {order.repeat_interval > 1 && ` (간격: ${order.repeat_interval})`}
                        </div>
                      )}
                      <div style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}>
                        상태: {order.status === 'pending' ? '대기중' : order.status === 'executed' ? '실행완료' : order.status === 'cancelled' ? '취소됨' : '실패'}
                      </div>
                    </div>
                    {order.status === 'pending' && (
                      <button 
                        onClick={async () => {
                          if (!window.confirm('예약 주문을 취소하시겠습니까?')) {
                            return;
                          }
                          try {
                            await cancelScheduledOrder(order._id);
                            // 예약 주문 목록 새로고침
                            await fetchScheduledOrders();
                            // 포트폴리오도 새로고침
                            await fetchPortfolio();
                          } catch (error) {
                            console.error('예약 주문 취소 실패:', error);
                            alert('예약 주문 취소에 실패했습니다.');
                          }
                        }}
                        style={{ 
                          padding: '6px 12px', 
                          fontSize: '12px',
                          backgroundColor: '#dc3545', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '4px', 
                          cursor: 'pointer',
                          marginLeft: '10px'
                        }}
                      >
                        취소
                      </button>
                    )}
                    {order.status === 'executed' && (
                      <span style={{ 
                        fontSize: '12px', 
                        color: '#28a745',
                        marginLeft: '10px',
                        fontWeight: 'bold'
                      }}>
                        ✓ 실행완료
                      </span>
                    )}
                    {order.status === 'failed' && (
                      <span style={{ 
                        fontSize: '12px', 
                        color: '#dc3545',
                        marginLeft: '10px'
                      }}>
                        ✗ 실행실패
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ margin: 0, color: '#999', textAlign: 'center', padding: '20px' }}>
                예약 주문이 없습니다.
              </p>
            )}
          </>
        )}
      </div>
      
      {/* AI 자동매도 옵션 모달 */}
      {showAutoSellModal && selectedSymbolForAutoSell && (
        <AutoSellOptionsModal
          symbol={selectedSymbolForAutoSell}
          stockName={selectedStockNameForAutoSell}
          onClose={() => {
            setShowAutoSellModal(false);
            setSelectedSymbolForAutoSell(null);
            setSelectedStockNameForAutoSell(null);
          }}
          authenticatedFetch={authenticatedFetch}
          onSave={async () => {
            // 설정 저장 후 포트폴리오 새로고침
            await fetchPortfolio();
          }}
        />
      )}

    </div>
  );
};

export default PortfolioTab;

